import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { VdpElement } from '../../types';
import { 
  Lock, 
  Unlock, 
  Copy, 
  Trash2, 
  ArrowUp, 
  ArrowDown, 
  Maximize2, 
  GripVertical, 
  Pin, 
  PinOff, 
  RotateCcw, 
  FolderPlus, 
  FolderMinus,
  MousePointer, 
  Ban, 
  Hand, 
  Eye, 
  Combine
} from 'lucide-react';
import { SimplifyPathIcon } from './SimplifyPathIcon';

// --- Alignment & Distribution SVG Icons (Crisp Adobe/Figma style) ---
const AlignLeftIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="2" x2="3" y2="22" strokeWidth="2.5" />
    <rect x="3" y="5" width="14" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="3" y="14" width="9" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignCenterHIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="2" x2="12" y2="22" strokeDasharray="2 2" strokeWidth="1.5" />
    <rect x="5" y="5" width="14" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="8" y="14" width="8" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignRightIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="21" y1="2" x2="21" y2="22" strokeWidth="2.5" />
    <rect x="7" y="5" width="14" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="12" y="14" width="9" height="5" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignTopIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="2" y1="3" x2="22" y2="3" strokeWidth="2.5" />
    <rect x="5" y="3" width="5" height="14" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="14" y="3" width="5" height="9" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignMiddleVIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="2" y1="12" x2="22" y2="12" strokeDasharray="2 2" strokeWidth="1.5" />
    <rect x="5" y="5" width="5" height="14" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="14" y="8" width="5" height="8" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const AlignBottomIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="2" y1="21" x2="22" y2="21" strokeWidth="2.5" />
    <rect x="5" y="7" width="5" height="14" rx="1" fill="currentColor" fillOpacity="0.25" />
    <rect x="14" y="12" width="5" height="9" rx="1" fill="currentColor" fillOpacity="0.25" />
  </svg>
);

const DistributeTopIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1.5H13V2.5H1V1.5ZM3 4.5H6V11.5H3V4.5ZM8 4.5H11V8.5H8V4.5Z" fill="currentColor"/>
  </svg>
);
const DistributeCenterVIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 6.5H13V7.5H1V6.5ZM3 2H6V12H3V2ZM8 3.5H11V10.5H8V3.5Z" fill="currentColor" fillOpacity="0.5"/>
    <path d="M1 6.5H13V7.5H1V6.5Z" fill="currentColor"/>
  </svg>
);
const DistributeBottomIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 11.5H13V12.5H1V11.5ZM3 2.5H6V9.5H3V2.5ZM8 5.5H11V9.5H8V5.5Z" fill="currentColor"/>
  </svg>
);
const DistributeLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.5 1V13H2.5V1H1.5ZM4.5 3H11.5V6H4.5V3ZM4.5 8H8.5V11H4.5V8Z" fill="currentColor"/>
  </svg>
);
const DistributeCenterHIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.5 1V13H7.5V1H6.5ZM2 3H12V6H2V3ZM3.5 8H10.5V11H3.5V8Z" fill="currentColor" fillOpacity="0.5"/>
    <path d="M6.5 1V13H7.5V1H6.5Z" fill="currentColor"/>
  </svg>
);
const DistributeRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 1V13H12.5V1H11.5ZM2.5 3H9.5V6H2.5V3ZM5.5 8H9.5V11H5.5V8Z" fill="currentColor"/>
  </svg>
);
const DistributeSpacingVIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1.5H13V2.5H1V1.5ZM1 11.5H13V12.5H1V11.5ZM3 4H11V9.5H3V4Z" fill="currentColor"/>
  </svg>
);
const DistributeSpacingHIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.5 1V13H2.5V1H1.5ZM11.5 1V13H12.5V1H11.5ZM4 3H9.5V11H4V3Z" fill="currentColor"/>
  </svg>
);

export interface UnifiedContextualToolbarProps {
  // Canvas Quick Tools
  isPreviewActive: boolean;
  onTogglePreview: () => void;
  isHandToolActive: boolean;
  onToggleHandTool: () => void;
  onResetView?: () => void;
  onOpenLivePreviewModal?: () => void;
  onOpenPathfinderPanel?: () => void;
  isPathfinderOpen?: boolean;
  onOpenSimplifyPath?: () => void;
  isSimplifyPathOpen?: boolean;

  // Selected Elements & Alignment Tools
  selectedElements: VdpElement[];
  canvasWidth: number;
  canvasHeight: number;
  onBatchUpdateElements: (updatesMap: Record<string, Partial<VdpElement>>) => void;
  onToggleSkipDisable?: () => void;
  onDuplicateSelected?: () => void;
  onDeleteSelected?: () => void;
  onMoveLayerUp?: () => void;
  onMoveLayerDown?: () => void;
  onToggleLockSelected?: () => void;
  onGroupSelected?: () => void;
  onUngroupSelected?: () => void;
}

const STORAGE_KEY = 'vdp_unified_contextual_bar_pos_v2';

export const UnifiedContextualToolbar: React.FC<UnifiedContextualToolbarProps> = React.memo(({
  isPreviewActive,
  onTogglePreview,
  isHandToolActive,
  onToggleHandTool,
  onResetView,
  onOpenLivePreviewModal,
  onOpenPathfinderPanel,
  isPathfinderOpen,
  onOpenSimplifyPath,
  isSimplifyPathOpen,
  selectedElements,
  canvasWidth,
  canvasHeight,
  onBatchUpdateElements,
  onToggleSkipDisable,
  onDuplicateSelected,
  onDeleteSelected,
  onMoveLayerUp,
  onMoveLayerDown,
  onToggleLockSelected,
  onGroupSelected,
  onUngroupSelected,
}) => {
  // Freeform persistent position
  const [position, setPosition] = useState<{ x: number; y: number } | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
          const clampedX = Math.max(10, Math.min((window.innerWidth || 1200) - 100, parsed.x));
          const clampedY = Math.max(10, Math.min((window.innerHeight || 800) - 60, parsed.y));
          return { x: clampedX, y: clampedY };
        }
      }
    } catch {}
    return null;
  });

  const [alignMode, setAlignMode] = useState<'selection' | 'canvas'>('canvas');
  const [isDragging, setIsDragging] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Save position to localStorage on update
  const savePosition = (pos: { x: number; y: number } | null) => {
    setPosition(pos);
    try {
      if (pos) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {}
  };

  // Keep position clamped if window resizes
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => {
        if (!prev) return null;
        const newX = Math.max(10, Math.min(window.innerWidth - 200, prev.x));
        const newY = Math.max(10, Math.min(window.innerHeight - 70, prev.y));
        return { x: newX, y: newY };
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePointerDownDrag = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isPinned || !toolbarRef.current) return;

    const rect = toolbarRef.current.getBoundingClientRect();
    const currX = position ? position.x : rect.left;
    const currY = position ? position.y : rect.top;

    if (!position) {
      savePosition({ x: rect.left, y: rect.top });
    }

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: currX,
      initialY: currY,
    };
    setIsDragging(true);

    const handlePointerMove = (moveEvt: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = moveEvt.clientX - dragRef.current.startX;
      const dy = moveEvt.clientY - dragRef.current.startY;
      const nextX = Math.max(10, Math.min(window.innerWidth - 200, dragRef.current.initialX + dx));
      const nextY = Math.max(10, Math.min(window.innerHeight - 70, dragRef.current.initialY + dy));
      setPosition({ x: nextX, y: nextY });
    };

    const handlePointerUp = (upEvt: PointerEvent) => {
      setIsDragging(false);
      if (dragRef.current) {
        const dx = upEvt.clientX - dragRef.current.startX;
        const dy = upEvt.clientY - dragRef.current.startY;
        const finalX = Math.max(10, Math.min(window.innerWidth - 200, dragRef.current.initialX + dx));
        const finalY = Math.max(10, Math.min(window.innerHeight - 70, dragRef.current.initialY + dy));
        savePosition({ x: finalX, y: finalY });
      }
      dragRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleResetPosition = (e: React.MouseEvent) => {
    e.stopPropagation();
    savePosition(null);
    setIsPinned(false);
  };

  // --- Element Contextual Alignment Calculations ---
  const hasSelection = selectedElements.length > 0;
  const isAnySelectedDisabled = hasSelection && selectedElements.some(el => el.disabled || el.isSkipped);
  const allLocked = hasSelection && selectedElements.every(e => e.locked);
  const targetMode = selectedElements.length === 1 ? 'canvas' : alignMode;
  const cWidth = canvasWidth || 300;
  const cHeight = canvasHeight || 200;

  const handleAlignLeft = () => {
    if (!hasSelection) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      selectedElements.forEach(el => { updatesMap[el.id] = { x: 0 }; });
    } else {
      const minX = Math.min(...selectedElements.map(e => e.x));
      selectedElements.forEach(el => { updatesMap[el.id] = { x: minX }; });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleAlignCenterH = () => {
    if (!hasSelection) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        const w = el.width || 100;
        updatesMap[el.id] = { x: Math.round((cWidth - w) / 2) };
      });
    } else {
      const minX = Math.min(...selectedElements.map(e => e.x));
      const maxX = Math.max(...selectedElements.map(e => e.x + (e.width || 100)));
      const centerX = minX + (maxX - minX) / 2;
      selectedElements.forEach(el => {
        const w = el.width || 100;
        updatesMap[el.id] = { x: Math.round(centerX - w / 2) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleAlignRight = () => {
    if (!hasSelection) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        const w = el.width || 100;
        updatesMap[el.id] = { x: Math.round(cWidth - w) };
      });
    } else {
      const maxX = Math.max(...selectedElements.map(e => e.x + (e.width || 100)));
      selectedElements.forEach(el => {
        const w = el.width || 100;
        updatesMap[el.id] = { x: Math.round(maxX - w) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleAlignTop = () => {
    if (!hasSelection) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      selectedElements.forEach(el => { updatesMap[el.id] = { y: 0 }; });
    } else {
      const minY = Math.min(...selectedElements.map(e => e.y));
      selectedElements.forEach(el => { updatesMap[el.id] = { y: minY }; });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleAlignMiddleV = () => {
    if (!hasSelection) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        const h = el.height || 100;
        updatesMap[el.id] = { y: Math.round((cHeight - h) / 2) };
      });
    } else {
      const minY = Math.min(...selectedElements.map(e => e.y));
      const maxY = Math.max(...selectedElements.map(e => e.y + (e.height || 100)));
      const centerY = minY + (maxY - minY) / 2;
      selectedElements.forEach(el => {
        const h = el.height || 100;
        updatesMap[el.id] = { y: Math.round(centerY - h / 2) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleAlignBottom = () => {
    if (!hasSelection) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      selectedElements.forEach(el => {
        const h = el.height || 100;
        updatesMap[el.id] = { y: Math.round(cHeight - h) };
      });
    } else {
      const maxY = Math.max(...selectedElements.map(e => e.y + (e.height || 100)));
      selectedElements.forEach(el => {
        const h = el.height || 100;
        updatesMap[el.id] = { y: Math.round(maxY - h) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleDistributeH = () => {
    if (!hasSelection) return;
    const sorted = [...selectedElements].sort((a, b) => a.x - b.x);
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      const sumWidth = sorted.reduce((acc, el) => acc + (el.width || 100), 0);
      const freeSpace = cWidth - sumWidth;
      const gap = freeSpace / (sorted.length + 1);
      let currX = gap;
      sorted.forEach(el => {
        updatesMap[el.id] = { x: Math.round(currX) };
        currX += (el.width || 100) + gap;
      });
    } else {
      if (sorted.length < 2) return;
      const minX = sorted[0].x;
      const maxRight = sorted[sorted.length - 1].x + (sorted[sorted.length - 1].width || 100);
      const sumWidth = sorted.reduce((acc, el) => acc + (el.width || 100), 0);
      const freeSpace = (maxRight - minX) - sumWidth;
      const gap = freeSpace / (sorted.length - 1);
      let currX = minX;
      sorted.forEach((el, idx) => {
        if (idx === 0) updatesMap[el.id] = { x: minX };
        else if (idx === sorted.length - 1) updatesMap[el.id] = { x: maxRight - (el.width || 100) };
        else updatesMap[el.id] = { x: Math.round(currX) };
        currX += (el.width || 100) + gap;
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleDistributeV = () => {
    if (!hasSelection) return;
    const sorted = [...selectedElements].sort((a, b) => a.y - b.y);
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    if (targetMode === 'canvas') {
      const sumHeight = sorted.reduce((acc, el) => acc + (el.height || 100), 0);
      const freeSpace = cHeight - sumHeight;
      const gap = freeSpace / (sorted.length + 1);
      let currY = gap;
      sorted.forEach(el => {
        updatesMap[el.id] = { y: Math.round(currY) };
        currY += (el.height || 100) + gap;
      });
    } else {
      if (sorted.length < 2) return;
      const minY = sorted[0].y;
      const maxBottom = sorted[sorted.length - 1].y + (sorted[sorted.length - 1].height || 100);
      const sumHeight = sorted.reduce((acc, el) => acc + (el.height || 100), 0);
      const freeSpace = (maxBottom - minY) - sumHeight;
      const gap = freeSpace / (sorted.length - 1);
      let currY = minY;
      sorted.forEach((el, idx) => {
        if (idx === 0) updatesMap[el.id] = { y: minY };
        else if (idx === sorted.length - 1) updatesMap[el.id] = { y: maxBottom - (el.height || 100) };
        else updatesMap[el.id] = { y: Math.round(currY) };
        currY += (el.height || 100) + gap;
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleDistributeObjTop = () => {
    if (selectedElements.length < 3 && targetMode !== 'canvas') return;
    const sorted = [...selectedElements].sort((a, b) => a.y - b.y);
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      const gapTop = cHeight / sorted.length;
      sorted.forEach((el, i) => { updatesMap[el.id] = { y: Math.round(gapTop * i) }; });
    } else {
      const minY = sorted[0].y;
      const maxY = sorted[sorted.length - 1].y;
      const step = (maxY - minY) / (sorted.length - 1);
      sorted.forEach((el, i) => {
        if (i === 0 || i === sorted.length - 1) return;
        updatesMap[el.id] = { y: Math.round(minY + step * i) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleDistributeObjCenterV = () => {
    if (selectedElements.length < 3 && targetMode !== 'canvas') return;
    const sorted = [...selectedElements].sort((a, b) => (a.y + (a.height||100)/2) - (b.y + (b.height||100)/2));
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      const step = cHeight / sorted.length;
      sorted.forEach((el, i) => { updatesMap[el.id] = { y: Math.round((step * i + step/2) - (el.height||100)/2) }; });
    } else {
      const minY = sorted[0].y + (sorted[0].height||100)/2;
      const maxY = sorted[sorted.length - 1].y + (sorted[sorted.length - 1].height||100)/2;
      const step = (maxY - minY) / (sorted.length - 1);
      sorted.forEach((el, i) => {
        if (i === 0 || i === sorted.length - 1) return;
        updatesMap[el.id] = { y: Math.round((minY + step * i) - (el.height||100)/2) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleDistributeObjBottom = () => {
    if (selectedElements.length < 3 && targetMode !== 'canvas') return;
    const sorted = [...selectedElements].sort((a, b) => (a.y + (a.height||100)) - (b.y + (b.height||100)));
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      const step = cHeight / sorted.length;
      sorted.forEach((el, i) => { updatesMap[el.id] = { y: Math.round((step * (i+1)) - (el.height||100)) }; });
    } else {
      const minY = sorted[0].y + (sorted[0].height||100);
      const maxY = sorted[sorted.length - 1].y + (sorted[sorted.length - 1].height||100);
      const step = (maxY - minY) / (sorted.length - 1);
      sorted.forEach((el, i) => {
        if (i === 0 || i === sorted.length - 1) return;
        updatesMap[el.id] = { y: Math.round((minY + step * i) - (el.height||100)) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleDistributeObjLeft = () => {
    if (selectedElements.length < 3 && targetMode !== 'canvas') return;
    const sorted = [...selectedElements].sort((a, b) => a.x - b.x);
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      const step = cWidth / sorted.length;
      sorted.forEach((el, i) => { updatesMap[el.id] = { x: Math.round(step * i) }; });
    } else {
      const minX = sorted[0].x;
      const maxX = sorted[sorted.length - 1].x;
      const step = (maxX - minX) / (sorted.length - 1);
      sorted.forEach((el, i) => {
        if (i === 0 || i === sorted.length - 1) return;
        updatesMap[el.id] = { x: Math.round(minX + step * i) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleDistributeObjCenterH = () => {
    if (selectedElements.length < 3 && targetMode !== 'canvas') return;
    const sorted = [...selectedElements].sort((a, b) => (a.x + (a.width||100)/2) - (b.x + (b.width||100)/2));
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      const step = cWidth / sorted.length;
      sorted.forEach((el, i) => { updatesMap[el.id] = { x: Math.round((step * i + step/2) - (el.width||100)/2) }; });
    } else {
      const minX = sorted[0].x + (sorted[0].width||100)/2;
      const maxX = sorted[sorted.length - 1].x + (sorted[sorted.length - 1].width||100)/2;
      const step = (maxX - minX) / (sorted.length - 1);
      sorted.forEach((el, i) => {
        if (i === 0 || i === sorted.length - 1) return;
        updatesMap[el.id] = { x: Math.round((minX + step * i) - (el.width||100)/2) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleDistributeObjRight = () => {
    if (selectedElements.length < 3 && targetMode !== 'canvas') return;
    const sorted = [...selectedElements].sort((a, b) => (a.x + (a.width||100)) - (b.x + (b.width||100)));
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (targetMode === 'canvas') {
      const step = cWidth / sorted.length;
      sorted.forEach((el, i) => { updatesMap[el.id] = { x: Math.round((step * (i+1)) - (el.width||100)) }; });
    } else {
      const minX = sorted[0].x + (sorted[0].width||100);
      const maxX = sorted[sorted.length - 1].x + (sorted[sorted.length - 1].width||100);
      const step = (maxX - minX) / (sorted.length - 1);
      sorted.forEach((el, i) => {
        if (i === 0 || i === sorted.length - 1) return;
        updatesMap[el.id] = { x: Math.round((minX + step * i) - (el.width||100)) };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const handleCenterCanvasBoth = () => {
    if (!hasSelection) return;
    const updatesMap: Record<string, Partial<VdpElement>> = {};
    if (selectedElements.length === 1) {
      const el = selectedElements[0];
      const w = el.width || 100;
      const h = el.height || 100;
      updatesMap[el.id] = {
        x: Math.round((cWidth - w) / 2),
        y: Math.round((cHeight - h) / 2)
      };
    } else {
      const minX = Math.min(...selectedElements.map(e => e.x));
      const maxX = Math.max(...selectedElements.map(e => e.x + (e.width || 100)));
      const minY = Math.min(...selectedElements.map(e => e.y));
      const maxY = Math.max(...selectedElements.map(e => e.y + (e.height || 100)));

      const groupW = maxX - minX;
      const groupH = maxY - minY;
      const targetMinX = (cWidth - groupW) / 2;
      const targetMinY = (cHeight - groupH) / 2;
      const dx = targetMinX - minX;
      const dy = targetMinY - minY;

      selectedElements.forEach(el => {
        updatesMap[el.id] = {
          x: Math.round(el.x + dx),
          y: Math.round(el.y + dy)
        };
      });
    }
    onBatchUpdateElements(updatesMap);
  };

  const toolbarContent = (
    <div
      ref={toolbarRef}
      style={
        position
          ? {
              position: 'fixed',
              left: `${position.x}px`,
              top: `${position.y}px`,
              transform: 'none',
              zIndex: 99999,
            }
          : {
              position: 'fixed',
              bottom: '24px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 99999,
            }
      }
      className={`flex items-center gap-1.5 flex-nowrap bg-zinc-900/95 backdrop-blur-md border ${
        isDragging
          ? 'border-blue-500 shadow-blue-500/25 ring-2 ring-blue-500/20'
          : isPinned
          ? 'border-amber-500/80 shadow-amber-500/20'
          : 'border-zinc-800'
      } shadow-2xl rounded-xl p-1.5 text-xs text-zinc-300 transition-shadow select-none pointer-events-auto max-w-[96vw] overflow-x-auto custom-scrollbar`}
    >
      {/* 1. Drag Handle (Freeform Dragging) */}
      <div
        onPointerDown={handlePointerDownDrag}
        className={`flex items-center justify-center p-1.5 rounded transition-colors ${
          isPinned 
            ? 'cursor-not-allowed opacity-40 text-zinc-600' 
            : 'cursor-grab active:cursor-grabbing text-zinc-400 hover:text-white hover:bg-zinc-800'
        }`}
        title={isPinned ? 'Toolbar is pinned (Unpin to drag)' : 'Drag toolbar anywhere (Freeform position)'}
      >
        <GripVertical className="w-4 h-4" />
      </div>

      <div className="w-[1px] h-6 bg-zinc-800 shrink-0" />

      {/* 2. Quick Canvas Tools (Preview, Live PDF, Hand Tool, Pathfinder, Reset View) */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Preview Button */}
        <button
          onClick={onTogglePreview}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
            isPreviewActive
              ? 'bg-zinc-800 text-white font-semibold shadow-inner border border-zinc-700/80 ring-1 ring-zinc-600/30'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60'
          }`}
          title="Toggle Canvas Preview mode (hides guidelines, grids, and selection bounding boxes)"
        >
          <MousePointer className={`w-3.5 h-3.5 ${isPreviewActive ? 'text-blue-400' : 'text-zinc-400'}`} />
          <span className="hidden sm:inline">Preview</span>
        </button>

        {/* Live PDF Preview Thumbnail Modal Button */}
        {onOpenLivePreviewModal && (
          <button
            onClick={onOpenLivePreviewModal}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-emerald-300 hover:text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition-all shadow-2xs"
            title="Open Live PDF Prepress Preview thumbnail to verify target dimensions and bleed"
          >
            <Eye className="w-3.5 h-3.5 text-emerald-400" />
            <span className="hidden sm:inline">Live PDF</span>
          </button>
        )}

        {/* Hand Tool Button */}
        <button
          onClick={onToggleHandTool}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
            isHandToolActive
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-xs'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60'
          }`}
          title="Hand / Pan Tool: Drag anywhere on canvas workspace to pan viewport (Or hold Middle-Click)"
        >
          <Hand className={`w-3.5 h-3.5 ${isHandToolActive ? 'text-amber-400' : 'text-zinc-400'}`} />
          <span className="hidden sm:inline">Hand Tool</span>
        </button>

        {/* Pathfinder Tool Button */}
        {onOpenPathfinderPanel && (
          <button
            onClick={onOpenPathfinderPanel}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              isPathfinderOpen
                ? 'bg-indigo-600 text-white shadow-md border border-indigo-500'
                : selectedElements.length >= 2
                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 hover:bg-indigo-500/30'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60'
            }`}
            title="Pathfinder Tool: Unite, Subtract, Intersect & Exclude vector shapes"
          >
            <Combine className={`w-3.5 h-3.5 ${isPathfinderOpen ? 'text-white' : 'text-indigo-400'}`} />
            <span className="hidden sm:inline">Pathfinder</span>
            {selectedElements.length >= 2 && (
              <span className="bg-indigo-500 text-white text-[9px] font-bold px-1.5 py-0.2 rounded-full ml-0.5">
                {selectedElements.length}
              </span>
            )}
          </button>
        )}

        {/* Simplify Path Tool Button */}
        {onOpenSimplifyPath && (
          <button
            onClick={onOpenSimplifyPath}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              isSimplifyPathOpen
                ? 'bg-emerald-600 text-white shadow-md border border-emerald-500'
                : selectedElements.some(e => e.type === 'pen' || e.type === 'path' || Boolean(e.points && e.points.length > 0) || Boolean(e.pathData || e.pathD))
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60'
            }`}
            title="Simplify Path Tool: Reduce anchor points & smooth bezier curves on complex vector shapes"
          >
            <SimplifyPathIcon className={`w-3.5 h-3.5 ${isSimplifyPathOpen ? 'text-white' : 'text-emerald-400'}`} />
            <span className="hidden sm:inline">Simplify</span>
          </button>
        )}

        {/* Reset Pan & Zoom Button */}
        {onResetView && (
          <button
            onClick={onResetView}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 transition-all"
            title="Reset Canvas View (Recenter pan position and 100% zoom)"
          >
            <RotateCcw className="w-3.5 h-3.5 text-cyan-400" />
            <span className="hidden md:inline">Reset View</span>
          </button>
        )}
      </div>

      <div className="w-[1px] h-6 bg-zinc-800 shrink-0" />

      {/* 3. Contextual Element Tools (Shown on the SAME bar & row) */}
      {hasSelection ? (
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Skip / Disable Button */}
          {onToggleSkipDisable && (
            <button
              onClick={onToggleSkipDisable}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                isAnySelectedDisabled
                  ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 shadow-xs'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60'
              }`}
              title={
                isAnySelectedDisabled
                  ? 'Enable selected element(s) in output'
                  : 'Skip/Disable selected element(s) in output and print'
              }
            >
              <Ban className={`w-3.5 h-3.5 ${isAnySelectedDisabled ? 'text-rose-400' : 'text-rose-400/80'}`} />
              <span className="hidden sm:inline">Skip/Disable</span>
              {isAnySelectedDisabled && (
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
              )}
            </button>
          )}

          {/* Align Mode Toggle (When 2+ elements selected) */}
          {selectedElements.length > 1 && (
            <div className="flex items-center bg-zinc-950/80 border border-zinc-800 rounded p-0.5 text-[10px]">
              <button
                onClick={() => setAlignMode('selection')}
                className={`px-1.5 py-0.5 rounded transition-colors ${
                  alignMode === 'selection' ? 'bg-blue-600 text-white font-medium' : 'text-zinc-400 hover:text-zinc-200'
                }`}
                title="Align relative to selection bounding box"
              >
                Sel
              </button>
              <button
                onClick={() => setAlignMode('canvas')}
                className={`px-1.5 py-0.5 rounded transition-colors ${
                  alignMode === 'canvas' ? 'bg-blue-600 text-white font-medium' : 'text-zinc-400 hover:text-zinc-200'
                }`}
                title="Align relative to canvas bounds"
              >
                Canvas
              </button>
            </div>
          )}

          {/* Align Objects Group (3x2 compact grid as shown in user image) */}
          <div className="flex flex-col gap-0.5 border border-zinc-800 rounded bg-zinc-950/70 p-0.5 shrink-0" title="Align Objects">
            <div className="flex items-center gap-0.5">
              <button
                onClick={handleAlignLeft}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-300 hover:text-white transition-colors"
                title="Align Left"
              >
                <AlignLeftIcon />
              </button>
              <button
                onClick={handleAlignCenterH}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-300 hover:text-white transition-colors"
                title="Align Center (Horizontal)"
              >
                <AlignCenterHIcon />
              </button>
              <button
                onClick={handleAlignRight}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-300 hover:text-white transition-colors"
                title="Align Right"
              >
                <AlignRightIcon />
              </button>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={handleAlignTop}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-300 hover:text-white transition-colors"
                title="Align Top"
              >
                <AlignTopIcon />
              </button>
              <button
                onClick={handleAlignMiddleV}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-300 hover:text-white transition-colors"
                title="Align Middle (Vertical)"
              >
                <AlignMiddleVIcon />
              </button>
              <button
                onClick={handleAlignBottom}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-300 hover:text-white transition-colors"
                title="Align Bottom"
              >
                <AlignBottomIcon />
              </button>
            </div>
          </div>

          {/* Distribute Objects Group (3x2 compact grid as shown in user image) */}
          <div className="flex flex-col gap-0.5 border border-zinc-800 rounded bg-zinc-950/70 p-0.5 shrink-0" title="Distribute Objects">
            <div className="flex items-center gap-0.5">
              <button
                onClick={handleDistributeObjLeft}
                disabled={selectedElements.length < 3 && targetMode !== 'canvas'}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Distribute Left Edges"
              >
                <DistributeLeftIcon />
              </button>
              <button
                onClick={handleDistributeObjCenterH}
                disabled={selectedElements.length < 3 && targetMode !== 'canvas'}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Distribute Horizontal Centers"
              >
                <DistributeCenterHIcon />
              </button>
              <button
                onClick={handleDistributeObjRight}
                disabled={selectedElements.length < 3 && targetMode !== 'canvas'}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Distribute Right Edges"
              >
                <DistributeRightIcon />
              </button>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={handleDistributeObjTop}
                disabled={selectedElements.length < 3 && targetMode !== 'canvas'}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Distribute Top Edges"
              >
                <DistributeTopIcon />
              </button>
              <button
                onClick={handleDistributeObjCenterV}
                disabled={selectedElements.length < 3 && targetMode !== 'canvas'}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Distribute Vertical Centers"
              >
                <DistributeCenterVIcon />
              </button>
              <button
                onClick={handleDistributeObjBottom}
                disabled={selectedElements.length < 3 && targetMode !== 'canvas'}
                className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Distribute Bottom Edges"
              >
                <DistributeBottomIcon />
              </button>
            </div>
          </div>

          {/* Distribute Spacing Group (Vertical & Horizontal Spacing) */}
          <div className="flex flex-col gap-0.5 items-center bg-zinc-950/70 border border-zinc-800 rounded p-0.5 shrink-0" title="Distribute Even Spacing">
            <button
              onClick={handleDistributeV}
              disabled={selectedElements.length < 3 && targetMode !== 'canvas'}
              className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              title="Distribute Vertical Space Evenly"
            >
              <DistributeSpacingVIcon />
            </button>
            <button
              onClick={handleDistributeH}
              disabled={selectedElements.length < 3 && targetMode !== 'canvas'}
              className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              title="Distribute Horizontal Space Evenly"
            >
              <DistributeSpacingHIcon />
            </button>
          </div>

          {/* Center Canvas Button */}
          <button
            onClick={handleCenterCanvasBoth}
            className="px-2.5 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/40 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 shrink-0 shadow-2xs"
            title="Center Selection Horizontally & Vertically on Canvas"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            <span className="text-[11px] font-medium hidden sm:inline whitespace-nowrap">Center Canvas</span>
          </button>

          {/* Extra Element Actions (Layers, Lock, Duplicate, Delete) */}
          <div className="flex items-center gap-0.5 shrink-0">
            {onGroupSelected && selectedElements.length >= 2 && (
              <button
                onClick={onGroupSelected}
                className="p-1.5 hover:bg-blue-600/20 hover:text-blue-400 rounded text-zinc-300 transition-colors flex items-center gap-1"
                title="Group Elements (Ctrl+G)"
              >
                <FolderPlus className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] hidden md:inline font-medium">Group</span>
              </button>
            )}
            {onUngroupSelected && selectedElements.some(e => Boolean(e.groupId)) && (
              <button
                onClick={onUngroupSelected}
                className="p-1.5 hover:bg-amber-600/20 hover:text-amber-400 rounded text-zinc-300 transition-colors flex items-center gap-1"
                title="Ungroup Elements (Ctrl+Shift+G)"
              >
                <FolderMinus className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[10px] hidden md:inline font-medium">Ungroup</span>
              </button>
            )}
            {onMoveLayerUp && (
              <button
                onClick={onMoveLayerUp}
                className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-zinc-200 transition-colors"
                title="Bring Forward"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
            )}
            {onMoveLayerDown && (
              <button
                onClick={onMoveLayerDown}
                className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-zinc-200 transition-colors"
                title="Send Backward"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
            )}
            {onToggleLockSelected && (
              <button
                onClick={onToggleLockSelected}
                className={`p-1.5 rounded transition-colors ${
                  allLocked 
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                    : 'hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200'
                }`}
                title={allLocked ? 'Unlock Selected' : 'Lock Selected'}
              >
                {allLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
              </button>
            )}
            {onDuplicateSelected && (
              <button
                onClick={onDuplicateSelected}
                className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-zinc-200 transition-colors"
                title="Duplicate Selection (Ctrl+D)"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            )}
            {onDeleteSelected && (
              <button
                onClick={onDeleteSelected}
                className="p-1.5 hover:bg-rose-500/20 hover:text-rose-400 rounded text-zinc-400 transition-colors"
                title="Delete Selection (Delete / Backspace)"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 shrink-0 px-1 text-zinc-500 text-[11px]">
          <span className="hidden lg:inline italic opacity-80">Select an element to align & organize</span>
        </div>
      )}

      <div className="w-[1px] h-6 bg-zinc-800 shrink-0" />

      {/* 4. Docking, Pin & Reset Position Controls */}
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          onClick={() => setIsPinned(!isPinned)}
          className={`p-1.5 rounded transition-colors ${
            isPinned
              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40 shadow-xs'
              : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
          }`}
          title={isPinned ? 'Toolbar is pinned at this location' : 'Pin toolbar at current position'}
        >
          {isPinned ? <Pin className="w-3.5 h-3.5 fill-current" /> : <PinOff className="w-3.5 h-3.5" />}
        </button>

        {position && (
          <button
            onClick={handleResetPosition}
            className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-cyan-300 transition-colors"
            title="Reset toolbar position to default bottom-center"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(toolbarContent, document.body);
});
